import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { services, capabilities } from '../mock';

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="bg-[#FAF7F5]">
      <HeroBanner
        image="/hero/services.jpg"
        overlay="default"
        height="min-h-[460px] lg:min-h-[540px]"
      >
        <div className="pt-[120px] lg:pt-[140px] pb-14 lg:pb-16">
          <Container>
            <div className="erbity-fade-up">
              <EyebrowLabel>Services</EyebrowLabel>
              <SectionTitle as="h1" className="mt-6 text-[44px] lg:text-[68px] leading-[1.04] max-w-4xl">
                Staffing solutions <span className="italic text-[#7E2E4A]">engineered</span> for tech.
              </SectionTitle>
              <p className="mt-6 text-[17px] text-[#3B333A] max-w-2xl leading-relaxed erbity-fade-up-1">
                Three engagement formats designed to match the way modern semiconductor,
                automotive and aerospace teams actually hire &mdash; single hires,
                project pods or a full recruitment engine.
              </p>
            </div>
          </Container>
        </div>
      </HeroBanner>

      <section className="pb-24">
        <Container>
          <div className="space-y-6">
            {services.map((s, i) => (
              <div key={s.id} id={s.id} className="grid lg:grid-cols-12 gap-8 bg-white rounded-3xl border border-[#EADDE2] p-8 lg:p-12 scroll-mt-32">
                <div className="lg:col-span-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">0{i + 1} &middot; Service</div>
                  <h2 className="mt-4 font-serif text-[34px] lg:text-[44px] leading-tight text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{s.title}</h2>
                  <p className="mt-2 text-[15px] italic text-[#7E2E4A]">{s.tagline}</p>
                  <p className="mt-6 text-[15.5px] text-[#4E434A] leading-relaxed">{s.description}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[13px] px-3 py-2 rounded-full bg-[#F2E3E8] text-[#7E2E4A] border border-[#EADDE2]">
                    {s.meta}
                  </div>
                </div>
                <div className="lg:col-span-7 border-t lg:border-t-0 lg:border-l border-[#EADDE2] pt-8 lg:pt-0 lg:pl-12">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A6A72] mb-4">Included capabilities</div>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-[15px] text-[#1A1420]">
                        <span className="w-6 h-6 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A]"><Check size={13} /></span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link to="/contact" className="group inline-flex items-center gap-2 text-[14px] font-medium text-[#1A1420] border-b border-[#1A1420]/30 hover:border-[#7E2E4A] pb-1">
                      Talk about a {s.title.toLowerCase()} program <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 lg:py-20 bg-white border-y border-[#EADDE2]">
        <Container>
          <div className="max-w-2xl mb-10">
            <EyebrowLabel>Roles we staff</EyebrowLabel>
            <SectionTitle className="mt-5 text-[32px] lg:text-[44px]">Every discipline in the silicon design stack.</SectionTitle>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {capabilities.map((c) => (
              <div key={c} className="px-4 py-4 rounded-xl bg-[#FAF7F5] border border-[#EADDE2] text-[13.5px] text-[#1A1420] hover:border-[#C4506F] transition-colors">
                {c}
              </div>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
